import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SalesTimeframeCards = () => {
  const [timeframeData, setTimeframeData] = useState({
    daily: null,
    weekly: null,
    monthly: null,
    yearly: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [dailyRes, weeklyRes, monthlyRes, yearlyRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/transaction-daily`),
          axios.get(`${API_BASE_URL}/transaction-weekly`),
          axios.get(`${API_BASE_URL}/transaction-monthly`),
          axios.get(`${API_BASE_URL}/transaction-yearly`)
        ]);

        setTimeframeData({
          daily: dailyRes.data,
          weekly: weeklyRes.data,
          monthly: monthlyRes.data,
          yearly: yearlyRes.data
        });
        
      } catch (err) {
        setError(err.message);
        console.error('Error fetching sales data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Sales Overview</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Sales Overview</h2>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error loading sales data: {error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate percentage changes (you might want to fetch previous period data for accurate changes)
  // For now, we'll just show static changes as in your original example
  const timeframes = [
    { 
      title: 'Today', 
      value: timeframeData.daily ? `₹${timeframeData.daily.total_earnings.toLocaleString()}` : '₹0', 
      change: '', 
      timeframe: 'vs yesterday' 
    },
    { 
      title: 'This Week', 
      value: timeframeData.weekly ? `₹${timeframeData.weekly.total_earnings.toLocaleString()}` : '₹0', 
      change: '', 
      timeframe: 'vs last week' 
    },
    { 
      title: 'This Month', 
      value: timeframeData.monthly ? `₹${timeframeData.monthly.total_earnings.toLocaleString()}` : '₹0', 
      change: '', 
      timeframe: 'vs last month' 
    },
    { 
      title: 'This Year', 
      value: timeframeData.yearly ? `₹${timeframeData.yearly.total_earnings.toLocaleString()}` : '₹0', 
      change: '', 
      timeframe: 'vs last year' 
    }
  ];

  return (
    <div className="mt-8">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Sales Overview</h2>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {timeframes.map((item, index) => (
          <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-sm font-medium text-gray-500">{item.title}</h3>
              <div className="mt-2 flex items-baseline">
                <span className="text-2xl font-semibold text-gray-900">{item.value}</span>
                <span className={`ml-2 text-sm font-semibold ${
                  item.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {item.change}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">{item.timeframe}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SalesTimeframeCards;